import { setItemInLocalStorage } from './localstorage';

/**
 * Masquerading swaps the signed-in user's tokens for a target account's, keeping the originals so
 * the session can be handed back. Mirrors the dashboard
 * (components/accounts-list/accounts-list-item.js and main-layout/navbar/navbar.js).
 *
 * Two things make the shape matter:
 *  - getUsersPermissions() in helpers/utils reads api_tokens.permissions straight out of
 *    localStorage, so permissions follow the swap automatically - but only once localStorage is
 *    written. Always persist through applyMasqueradeUser below rather than dispatching setUser
 *    alone, or the UI and the permission checks disagree.
 *  - api_tokens carries the bearer token used by store/services, while original_api_tokens is what
 *    store/services/original-token-api.js reads. That second client exists so an already
 *    masqueraded session can masquerade into a *different* account: the masqueraded token cannot
 *    itself masquerade.
 */

/**
 * @param user the currently signed-in user object
 * @param account the account being entered (needs at least id and name)
 * @param response the POST /organizations/{id}/masquerade body
 * @param availableAccounts optional accounts to offer in the sidebar switcher
 */
export const buildMasqueradeUser = (user, account, response, availableAccounts = []) => {
  // Already masquerading: keep the FIRST originals, otherwise switching accounts would overwrite
  // them with the masqueraded ones and exiting would never get the real session back.
  const originalApiTokens = user.original_api_tokens || user.api_tokens;
  const originalOrganizations = user.original_organizations || user.organizations;
  const originalLicenses = user.original_licenses || user.licenses || [];

  const switcherAccounts = availableAccounts.length ? availableAccounts : user.masquarage_organizations || [account];

  return {
    ...user,
    original_api_tokens: { ...originalApiTokens },
    original_organizations: originalOrganizations,
    original_licenses: originalLicenses,
    api_tokens: { ...response.api_tokens },
    organizations: response.organizations,
    licenses: response.licenses,
    accountDetails: account,
    selectedAccount: { ...account },
    masquarage_organizations: switcherAccounts,
    isAccessAccount: true,
  };
};

export const buildExitMasqueradeUser = user => ({
  ...user,
  api_tokens: { ...user.original_api_tokens },
  organizations: user.original_organizations,
  licenses: user.original_licenses,
  accountDetails: null,
  selectedAccount: null,
  original_api_tokens: null,
  original_organizations: null,
  original_licenses: null,
  masquarage_organizations: null,
  isAccessAccount: false,
});

/** Persists first, then updates the store, so permission reads never see the stale token. */
export const applyMasqueradeUser = (dispatch, setUser, updatedUser) => {
  setItemInLocalStorage('user', updatedUser);
  dispatch(setUser(updatedUser));
};
